/**
 * components/Grid
 * ──────────────────────────────────────────────────────────────────────────
 * `repeat(auto-fit, minmax(min, 1fr))`, reimplemented for React Native.
 *
 * The design lays out every card group this way — KPI cards at minmax(210px),
 * chart cards at minmax(330px), the custodian columns at minmax(300px),
 * reconciliation legs at minmax(150px). Reproducing the CSS behaviour in one
 * component rather than hand-rolling flex-basis per screen is what makes the
 * reflow identical everywhere, and it is also the whole of this app's
 * responsive story for card grids: the same minmax that looks like four
 * columns on a desktop is one column on a phone, with no breakpoint involved.
 *
 * The container measures itself with `onLayout` (RN has no CSS Grid, so the
 * column count cannot be resolved at style time). Before the first measurement
 * children render at full width, which is the correct single-column fallback
 * rather than a flash of zero-width cards.
 * ──────────────────────────────────────────────────────────────────────────
 */
import { Children, useState, type ReactNode } from 'react';
import { StyleSheet, View, type LayoutChangeEvent, type ViewStyle } from 'react-native';
import { autoFitColumns } from '../../lib/responsive';

export interface GridProps {
  children: ReactNode;
  /** The `minmax()` minimum — the narrowest a column may be before wrapping. */
  min: number;
  /** Gap between columns and rows. */
  gap: number;
  /**
   * `align-items: start`. The custodian screen needs it so a short card does
   * not stretch to match a tall one beside it.
   */
  alignStart?: boolean;
  style?: ViewStyle;
}

export function Grid({ children, min, gap, alignStart = false, style }: GridProps) {
  const [available, setAvailable] = useState(0);

  const onLayout = (event: LayoutChangeEvent) => {
    const next = Math.round(event.nativeEvent.layout.width);
    // Guard the state write: onLayout fires on every parent re-layout, and an
    // unconditional setState here would loop through the reflow it causes.
    if (next !== available) setAvailable(next);
  };

  const items = Children.toArray(children).filter(Boolean);
  // The child count is what collapses the unused tracks — see autoFitColumns.
  const { width } =
    available > 0 ? autoFitColumns(available, min, gap, items.length) : { width: available };

  return (
    <View style={[styles.grid, { gap }, alignStart && styles.alignStart, style]} onLayout={onLayout}>
      {items.map((child, index) => (
        <View
          // Index is a safe key here: the children of a grid are a fixed,
          // ordered set of cards, never a reordered list.
          key={index}
          style={available > 0 ? { width } : styles.fullWidth}
        >
          {child}
        </View>
      ))}
    </View>
  );
}

const styles = StyleSheet.create({
  grid: { flexDirection: 'row', flexWrap: 'wrap', width: '100%' },
  alignStart: { alignItems: 'flex-start' },
  fullWidth: { width: '100%' },
});

export default Grid;
